import React from "react";
import { Tooltip, ValueOrPlaceholder } from "@sscs/ui";
import {
  ApprovedIcon,
  AutoApprovedIcon,
  RejectedIcon,
  RevokedIcon,
  UndeterminedIcon,
} from "@sscs/ui/icons";
import { ORG_ROLE_TO_STRING } from "Constants";
import type { ApprovalInfo, ApprovalStatus } from "Types";
import { TimeStatus } from "../index";
import styles from "./approvalStatus.module.scss";

type ApprovalStatusSuffix = keyof {
  [K in keyof typeof styles as K extends `approval-status-${infer S}`
    ? S
    : never]: unknown;
};

type StatusInfo = {
  label: string;
  shortLabel: string;
  icon: React.ReactElement;
};

export const APPROVAL_STATUS_MAP: Record<ApprovalStatus, StatusInfo> = {
  ApprovalRejected: {
    label: "Approval rejected",
    icon: <RejectedIcon size="xs" />,
    shortLabel: "Rejected",
  },
  ApprovalRevoked: {
    label: "Approval revoked",
    icon: <RevokedIcon size="xs" />,
    shortLabel: "Revoked",
  },
  Approved: {
    label: "Approved",
    icon: <ApprovedIcon size="xs" />,
    shortLabel: "Approved",
  },
  ApprovedAutomatically: {
    label: "Approved automatically",
    icon: <AutoApprovedIcon size="xs" />,
    shortLabel: "Approved",
  },
  Undetermined: {
    label: "Requires approval",
    icon: <UndeterminedIcon size="xs" />,
    shortLabel: "Pending",
  },
} as const;

const STYLES_MAP = {
  Approved: "approved",
  ApprovedAutomatically: "approved",
  ApprovalRejected: "rejected",
  ApprovalRevoked: "revoked",
  Undetermined: "undetermined",
} as const satisfies Record<ApprovalStatus, ApprovalStatusSuffix>;

const PendingFrame = (): React.JSX.Element => (
  <svg
    className={styles["pending-frame"]}
    viewBox="0 0 108 26"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <rect
      x=".75"
      y=".75"
      width="106.5"
      height="24.5"
      rx="3.25"
      strokeDasharray="3 3"
    />
    <line
      x1="26.75"
      y1="4.25"
      x2="26.75"
      y2="21.75"
      strokeDasharray="3.5 3.5"
    />
  </svg>
);

type StatusProps = {
  status: ApprovalStatus;
  info: ApprovalInfo | null;
  variant?: "badge" | "text";
};

const ApprovalStatusIcon = ({
  status,
  info,
  variant = "badge",
}: StatusProps): React.JSX.Element => {
  const { label, icon, shortLabel } = APPROVAL_STATUS_MAP[status];

  const badge = (
    <div
      className={styles[`approval-status-${STYLES_MAP[status]}`]}
      data-test-id="approval-status"
      data-variant={variant}
    >
      {status === "Undetermined" && variant === "badge" && <PendingFrame />}
      <span className={styles["icon-cell"]}>{icon}</span>
      <span className={styles.label}>{shortLabel}</span>
    </div>
  );

  if (status === "Undetermined") {
    return badge;
  }

  return (
    <Tooltip
      trigger={variant === "text" ? "term" : "graphic"}
      tooltipType="card"
      header={label}
      headerClassName={styles[`band-${STYLES_MAP[status]}`]}
      title={info?.reason}
      footer={
        info && (
          <dl className={styles.info}>
            <dt>User: </dt>
            <dd>{info.user}</dd>
            <dt>Role: </dt>
            <dd>
              <ValueOrPlaceholder
                value={info.role ? ORG_ROLE_TO_STRING[info.role] : undefined}
              />
            </dd>
            <dt>Time: </dt>
            <dd>
              <TimeStatus timestamp={info.timestamp} />
            </dd>
          </dl>
        )
      }
    >
      {badge}
    </Tooltip>
  );
};

export default ApprovalStatusIcon;
